/**
 * Traducciones de columna a valor del CRM, compartidas por TODOS los
 * importadores.
 *
 * POR QUE ESTAN AQUI Y NO EN CADA PANTALLA. Estas tres funciones vivian dentro
 * de ImportView.jsx, encerradas en el componente, asi que el importador de
 * monday no podia usarlas: tenia sus propias versiones o directamente no
 * traducia el campo. Eso no es un detalle de estilo — es como se cuela un
 * fallo en la mitad de los caminos y no en la otra. La trampa de "inactivo"
 * contiene "activo" ya la conocia una de las copias y la otra no.
 *
 * Regla: si un importador nuevo necesita traducir sexo, si-fuma o el estatus
 * del cliente, importa de aqui. No se copia.
 */

const t = (s) => String(s == null ? '' : s).trim().toLowerCase();

/**
 * Sexo. Devuelve "Male" / "Female" con la caja EXACTA que guarda la ficha, o
 * cadena vacia si la celda no dice ninguno de los dos — dejarlo vacio es mejor
 * que adivinar el sexo de una persona.
 */
export function mapearSexo(v) {
  const s = t(v);
  if (/^(m|male|masculino|hombre|varon|varón)$/.test(s) || /masculin|hombre/.test(s)) return 'Male';
  if (/^(f|female|femenino|mujer)$/.test(s) || /femenin|mujer/.test(s)) return 'Female';
  return '';
}

/**
 * ¿Fuma? Tres estados: "yes", "no", y "" = *no se ha preguntado*, que NO es lo
 * mismo que "no fuma". Una celda vacia del Excel no puede convertirse en una
 * respuesta que el cliente nunca dio.
 *
 * La NEGACION se prueba PRIMERO: "no fuma" contiene "fuma" y "non-smoker"
 * contiene "smoker", asi que al reves todo fumador negado entraria como
 * fumador. Es la misma trampa de "inactivo" conteniendo "activo".
 */
export function mapearFumador(v) {
  const s = t(v);
  if (!s) return '';
  if (/^(no|n|false|0)$/.test(s) || /^(no|sin)\b/.test(s) || /non.?smoker|no.?smoker|nunca|ex.?fumador/.test(s)) return 'no';
  if (/^(si|sí|s|y|yes|true|1)$/.test(s) || /fuma|smoker|tabaco|tobacco/.test(s)) return 'yes';
  return '';
}

/**
 * Estatus del cliente: active | inactive | prospect | referral.
 *
 * "inact" se prueba ANTES que "act" por lo mismo de arriba: "inactivo" contiene
 * "activo", y al reves toda la cartera archivada entraria como activa.
 *
 * Una celda VACIA cae en "active", que es el valor con el que entraban todos
 * antes de que esta columna se pudiera mapear: no cambia nada para quien no la
 * traiga.
 */
export function mapearEstatusDeCliente(v) {
  const s = t(v);
  if (!s) return 'active';
  if (/inact|archiv|cancel/.test(s)) return 'inactive';
  if (/prospect|lead|pend/.test(s)) return 'prospect';
  if (/refer/.test(s)) return 'referral';
  if (/act/.test(s)) return 'active';
  return 'active';
}

/**
 * Una cantidad de dinero escrita a mano -> solo el numero, en punto decimal.
 *
 * El separador de miles cambia de una persona a otra dentro del MISMO
 * documento: la planilla de registro que trajo esto escribia el subsidio
 * "$1,574.00" (a la inglesa) y la prima bruta "$1.606.49" (a la espanola).
 * Quitar todo menos digitos y puntos —que es lo que se hacia— convertia la
 * segunda en "1.606.49", que no es ningun numero y sale asi en la ficha.
 *
 * La regla que las distingue no es el simbolo, es CUANTOS DIGITOS lleva
 * detras el ultimo separador: dos son decimales, tres son miles. Con uno
 * solo ("1.5") tambien son decimales, porque nadie escribe miles asi.
 *
 * Sin separadores, o sin nada que parezca un numero, devuelve lo que habia
 * limpio de simbolos: es mejor ensenar lo que escribieron que un vacio.
 */
export function mapearDinero(v) {
  const s = String(v == null ? '' : v).trim();
  if (!s) return '';
  const soloNumero = s.replace(/[^0-9.,]/g, '');
  if (!/\d/.test(soloNumero)) return '';
  const ultimo = Math.max(soloNumero.lastIndexOf('.'), soloNumero.lastIndexOf(','));
  if (ultimo === -1) return soloNumero;
  const decimales = soloNumero.slice(ultimo + 1);
  const entero = soloNumero.slice(0, ultimo).replace(/[.,]/g, '');
  if (/^\d{1,2}$/.test(decimales)) return `${entero}.${decimales}`;
  return entero + decimales;
}

// ─── PARENTESCO ──────────────────────────────────────────────────────────────
//
// UNA SOLA LISTA DE PALABRAS PARA LOS CINCO IMPORTADORES.
//
// Habia CINCO clasificadores de parentesco, uno por camino, y ninguno decia lo
// mismo que otro:
//
//   apizealImport.js .............. spouse|espos|conyug|cónyug|wife|husband|marido
//   ImportView (Excel/CSV) ........ ...lo mismo + pareja
//   ImportView (PDF de solicitud) . SPOUSE|ESPOS[OA]
//   mondayImport.js ............... wife|husband|spouse|esposo|esposa|conyuge|conjuge|marido
//   MondayMigrationWizard.jsx ..... conyuge|conjuge|esposo|esposa|spouse|marido|wife|husband
//
// NINGUNO conocia la palabra "partner", y solo uno conocia "pareja". Esa
// diferencia de una palabra es exactamente lo que rompio la importacion de la
// agencia de Denisse (Lissette, 2026-09-15: *"varios spouse o partner se fueron
// como spouse pero al espacio de dependiente y no de spouse como tal"*): el
// lector de Apizeal mandaba al *Partner* al bloque de dependientes —para el no
// era conyuge— y despues ImportView, que SI conoce "pareja", le ponia la
// etiqueta `spouse`. Resultado: una persona en la lista de dependientes con el
// cartel de conyuge, y las casillas del conyuge vacias.
//
// Los acentos se quitan antes de mirar: "cónyuge" y "conyuge" son la misma
// palabra y la mitad de los archivos viene sin tildes (misma leccion que el
// slug del canal de chat, regla 171).

const sinAcentos = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
const tp = (v) => sinAcentos(String(v == null ? '' : v)).trim().toLowerCase();

// Un EX no es la pareja de hoy. "ex esposa", "ex-pareja", "former spouse":
// entra como un dependiente mas y nadie le reabre el matrimonio.
const EX = /(^|[^a-z])ex([^a-z]|$)|^ex[-\s]?(espos|conyug|conjug|pareja|spouse|wife|husband)|former|anterior/;
// Un socio no es una pareja. "business partner", "socio" — la unica palabra de
// esta lista que significa dos cosas.
const SOCIO = /business|socio|laboral|negocio|trabajo|work/;
const CASADO = /spouse|espos|conyug|conjug|marido|\bwife\b|\bhusband\b/;
const DE_HECHO = /partner|pareja|conviviente|concubin|union libre|unionlibre/;
const PADRE = /\bparent|padre|padrastro|madre|madrastra|mother|father|abuel/;
const HIJO = /\bson\b|\bsons\b|stepson|\bhij|daugh?ter|daugther|daugter|\bchild|children|menor|nieto|nieta|grandson|\bstep\b/;
const HERMANO = /\bsibling|hermano|hermana|\bbrother|\bsister/;

/**
 * La celda de parentesco -> la relacion del CRM.
 *
 * Devuelve '' cuando la celda esta VACIA, que no es lo mismo que "no la
 * reconozco": cada importador tiene su propio valor por defecto para el vacio
 * (el de Excel/CSV usa "child", que era el valor fijo de antes; los de monday y
 * Apizeal usan "dependent") y esa decision se queda en cada uno.
 *
 * Valores: 'spouse' | 'child' | 'parent' | 'sibling' | 'dependent' | ''.
 * `sibling` es una opcion real del formulario de la ficha y hasta ahora ningun
 * importador la ponia: un hermano entraba como "dependiente".
 */
export function mapearParentesco(v) {
  const s = tp(v);
  if (!s) return '';
  // El orden IMPORTA. Padre/madre ANTES que hijo: "stepfather" y
  // "grandmother" llevan dentro "step" y "mother", y al reves un padrastro
  // entraria como hijo.
  if (PADRE.test(s)) return 'parent';
  if (!EX.test(s) && !SOCIO.test(s) && (CASADO.test(s) || DE_HECHO.test(s))) return 'spouse';
  if (HIJO.test(s)) return 'child';
  if (HERMANO.test(s)) return 'sibling';
  return 'dependent';
}

/**
 * ¿Esta pareja es de HECHO (union libre) y no un matrimonio?
 *
 * Solo dice si, cuando la celda nombra una pareja sin casarse. Con eso el
 * importador escribe `domestic_partner` en vez de inventar un "casado/a" — que
 * es lo que se hacia para que la pareja se viera siquiera en pantalla, porque
 * hasta hoy `hasCurrentSpouse` solo aceptaba "married" (ver
 * crm-app/estadoCivil.js).
 *
 * Si la celda dice las dos cosas ("esposa / pareja"), gana el matrimonio: es el
 * dato mas fuerte de los dos.
 */
export function esParejaDeHecho(v) {
  const s = tp(v);
  if (!s || EX.test(s) || SOCIO.test(s)) return false;
  if (CASADO.test(s)) return false;
  return DE_HECHO.test(s);
}
